import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { AgencyService } from "src/app/shared/services/agency.service";
import { Agency } from "src/app/shared/types/user-types";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { PopupType } from "src/app/shared/enums/popup-types";

@Component({
  selector: "app-agency-profile",
  templateUrl: "./agency-profile.component.html",
  standalone: false,
})
export class AgencyProfileComponent implements OnInit {
  agencyForm!: FormGroup;
  editMode = false;
  isLoading = false;
  agencyData!: Agency;

  constructor(
    private fb: FormBuilder,
    private agencyService: AgencyService,
    public translate: TranslateService,
  ) {}

  ngOnInit() {
    this.initForm();
    this.loadAgencyData();
  }

  private initForm() {
    this.agencyForm = this.fb.group({
      name: [{ value: "", disabled: true }, Validators.required],
      email: [{ value: "", disabled: true }, [Validators.required, Validators.email]],
      city: [{ value: "", disabled: true }, Validators.required],
      postalCode: [{ value: "", disabled: true }, Validators.required],
      companyAddress: [{ value: "", disabled: true }, Validators.required],
      companyEmail: [{ value: "", disabled: true }, [Validators.required, Validators.email]],
      companyPhone: [{ value: "", disabled: true }, Validators.required],
      accountNumber: [{ value: "", disabled: true }, Validators.required],
      pib: [{ value: "", disabled: true }, [Validators.required, Validators.pattern(/^(\d{9})$/)]],
      mb: [{ value: "", disabled: true }, [Validators.required, Validators.pattern(/^(\d{8})$/)]],
      iban: [{ value: "", disabled: true }, Validators.required],
      swift: [{ value: "", disabled: true }, Validators.required],
    });
  }

  private loadAgencyData() {
    this.isLoading = true;
    const sub = this.agencyService.getAgency().subscribe({
      next: (data) => {
        sub.unsubscribe();
        this.isLoading = false;
        if (data) {
          this.agencyData = data;
          this.agencyForm.patchValue(data);
        }
      },
      error: (err) => {
        sub.unsubscribe();
        this.isLoading = false;
        showMessage(PopupType.Danger, err.message || "Failed to load agency settings.");
      },
    });
  }

  toggleEdit() {
    this.editMode = true;
    this.setFormState();
  }

  private setFormState() {
    Object.keys(this.agencyForm.controls).forEach((key) => {
      const control = this.agencyForm.get(key);
      if (this.editMode) {
        control?.enable();
      }
      else {
        control?.disable();
      }
    });
  }

  discardChanges() {
    this.editMode = false;
    this.agencyForm.reset();
    if (this.agencyData) {
      this.agencyForm.patchValue(this.agencyData);
    }
    this.setFormState();
  }

  saveChanges() {
    if (!this.agencyForm.valid) {
      this.agencyForm.markAllAsTouched();
      return;
    }

    const updatedAgency: Agency = {
      ...this.agencyData,
      ...this.agencyForm.getRawValue(),
    };

    this.isLoading = true;
    const sub = this.agencyService.updateAgency(updatedAgency).subscribe({
      next: (data) => {
        sub.unsubscribe();
        this.isLoading = false;
        this.agencyData = data;
        this.editMode = false;
        this.setFormState();
        showMessage(PopupType.Success, this.translate.instant("user_profile.agency_updated_successfully"));
      },
      error: (err) => {
        sub.unsubscribe();
        this.isLoading = false;
        showMessage(PopupType.Danger, err.message || "Failed to update agency settings.");
      },
    });
  }
}
